import { useRender } from '@base-ui/react/use-render';
import clsx from 'clsx';
import { splitNewline } from '@_/utils';

export type TextProps = React.PropsWithChildren<{
  block?: boolean;
  bold?: boolean;
  centered?: boolean;
  className?: string;
  dark?: boolean;
  italic?: boolean;
  render?: useRender.RenderProp;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  semibold?: boolean;
  subdued?: boolean;
  transformNewlineToBlocks?: boolean;
  uppercase?: boolean;
}>;

export function Text({
  block,
  bold,
  centered,
  children,
  className,
  dark = false,
  italic,
  render,
  semibold,
  size = 'sm',
  subdued,
  transformNewlineToBlocks,
  uppercase,
  ...rest
}: TextProps) {
  const textProps = {
    className: clsx(
      dark
        ? clsx(subdued ? 'text-gray-200' : 'text-white')
        : clsx(
            bold || semibold
              ? clsx(subdued ? 'text-gray-500' : 'text-gray-950')
              : clsx(subdued ? 'text-gray-500' : 'text-gray-800')
          ),
      bold ? 'font-bold' : semibold ? 'font-medium' : 'font-normal',
      centered && 'text-center',
      italic && 'italic',
      uppercase && 'uppercase',
      size === 'xs' && 'text-xs',
      size === 'sm' && 'text-sm',
      size === 'md' && 'text-base',
      size === 'lg' && 'text-lg',
      className
    ),
    ...rest
  };

  const blockElement = block || transformNewlineToBlocks;

  if (transformNewlineToBlocks && typeof children === 'string') {
    const lines = splitNewline(children).map((line, index) => ({
      key: `${line}-${index}`,
      content: line
    }));

    return (
      <div className="space-y-2 md:space-y-2.5">
        {lines.map(({ key, content }) => (
          <TextElement key={key} block={blockElement} render={render} {...textProps}>
            {content}
          </TextElement>
        ))}
      </div>
    );
  }

  return (
    <TextElement block={blockElement} render={render} {...textProps}>
      {children}
    </TextElement>
  );
}

function TextElement({
  block,
  render,
  ...props
}: useRender.ComponentProps<'span'> & { block?: boolean }) {
  return useRender({
    defaultTagName: block ? 'div' : 'span',
    render,
    props
  });
}
